<template>
  <div>
    <Navbar />
    <div class="container">
      <section class="service-hero">
        <p class="hero-kicker">Service Journey</p>
        <h1>A Complete Investment Service Journey from Consultation to Annual Review</h1>
        <p class="hero-summary">
          We structure our buyer-side service into 8 key stages so each step is trackable, actionable, and measurable.
        </p>
      </section>

      <!-- <section class="flow-section">
        <div class="flow-shell">
          <div class="flow-image-wrap">
            <img src="/images/ourservices.png" alt="Our services overview" loading="lazy" class="flow-image" />
          </div>
        </div>
      </section> -->

      <section class="stages-section">
        <article v-for="(stage, index) in stages" :key="`detail-${stage.id}`" class="stage-block" :class="{ 'stage-reverse': index % 2 === 1 }">
          <div class="stage-image-wrap">
            <img :src="stage.image" :alt="`${stage.title} image`" loading="lazy" />
          </div>

          <div class="stage-content">
            <p class="stage-step">Step {{ index + 1 }}</p>
            <h3>{{ stage.title }}</h3>
            <p class="stage-desc">{{ stage.description }}</p>
            <ul>
              <li v-for="point in stage.points" :key="`${stage.id}-${point}`">{{ point }}</li>
            </ul>
          </div>
        </article>
      </section>
    </div>
    <SiteFooter />
  </div>
</template>

<script>
import SiteFooter from '@/components/layout/SiteFooter.vue';
import Navbar from '@/components/layout/Navbar.vue';

export default {
  components: { SiteFooter, Navbar },
  data() {
    return {
      stages: [
        {
          id: 'initial-consultation',
          title: 'Initial Consultation',
          image: 'https://images.unsplash.com/photo-1551836022-d5d88e9218df?auto=format&fit=crop&w=1200&q=80',
          description: 'Review your cashflow, assets, risk profile, and target timeline to confirm buyer-side strategy fit.',
          points: ['15 min discovery call', 'Define target suburbs and budget boundaries', 'Identify key blind spots and priorities']
        },
        {
          id: 'portfolio-plan-meeting',
          title: 'Portfolio Plan Meeting',
          image: 'https://images.unsplash.com/photo-1460925895917-afdab827c52f?auto=format&fit=crop&w=1200&q=80',
          description: 'Build a data-driven portfolio roadmap aligned with your goals, stage by stage.',
          points: ['90 minutes online meeting', 'Create a 12-36 month portfolio plan', 'Map suburb, asset type, and return model', 'Set execution KPIs and review checkpoints']
        },
        {
          id: 'finance-application',
          title: 'Finance Application',
          image: 'https://images.unsplash.com/photo-1554224155-6726b3ff858f?auto=format&fit=crop&w=1200&q=80',
          description: 'Coordinate with lender to secure approvals that match your strategy.',
          points: ['Evaluate the proper finance structure', 'Refer to the proper lender to apply the finance', 'Coordinate with the lender to secure the approvals']
        },
        {
          id: 'property-acquisition',
          title: 'Property Acquisition',
          image: 'https://images.unsplash.com/photo-1560518883-ce09059eeffa?auto=format&fit=crop&w=1200&q=80',
          description: 'Execute shortlisting, due diligence, negotiation, and contract exchange for optimal outcomes.',
          points: ['Asset shortlisting and on-site verification', 'Multi-dimensional due diligence', 'Negotiation strategy and contract optimization']
        },
        {
          id: 'settlement-coordination',
          title: 'Settlement Coordination',
          image: 'https://images.unsplash.com/photo-1454165804606-c3d57bc86b40?auto=format&fit=crop&w=1200&q=80',
          description: 'Coordinate legal, lending, and agency milestones during settlement to avoid costly misses.',
          points: ['Critical timeline reminders and tracking', 'Document and compliance verification', 'Settlement risk checks and mitigation']
        },
        {
          id: 'property-management',
          title: 'Property Management',
          image: 'https://images.unsplash.com/photo-1560184897-ae75f418493e?auto=format&fit=crop&w=1200&q=80',
          description: 'During the holding phase, optimize rental performance, maintenance cost, and cashflow stability.',
          points: ['Rent strategy and vacancy optimization', 'Maintenance and expense control', 'Ongoing net-return tracking']
        },
        {
          id: 'annual-portfolio-review',
          title: 'Annual Portfolio Review Meeting',
          image: 'https://images.unsplash.com/photo-1450101499163-c8848c66ca85?auto=format&fit=crop&w=1200&q=80',
          description: 'Review annual portfolio performance and adjust strategy based on market shifts.',
          points: ['Annual return and risk review', 'Portfolio rebalancing recommendations', 'Action plan for the next 12 months']
        },
        {
          id: 'profit-secure',
          title: 'Profit Secure',
          image: 'https://images.unsplash.com/photo-1579621970563-ebec7560ff3e?auto=format&fit=crop&w=1200&q=80',
          description: 'At the right time, sell your highest-growth asset to lock in gains, manage tax, and fuel the next stage of portfolio growth.',
          points: ['Sell the high-growth property at the optimal 2-5 year window', 'Secure and crystallise capital gains', 'Plan and minimise tax obligations', 'Redeploy proceeds to expand the portfolio']
        }
      ]
    }
  }
}

</script>

<style scoped>
.container {
  --sv-bg: #f6f2ec;
  --sv-bg-soft: #fcfaf6;
  --sv-dark: #5c5147;
  --sv-primary: #8a7d70;
  --sv-accent: #c3ab90;
  --sv-text: #6a5f55;
  --sv-muted: #897d72;
  --sv-card-border: #e1d7ca;
  padding-top: 108px;
  background: linear-gradient(180deg, #faf8f4 0%, #f6f2ec 42%, #f2ece4 100%);
}

.service-hero {
  max-width: 1080px;
  margin: 0 auto;
  padding: 86px 5% 54px;
  text-align: center;
}

.hero-kicker {
  margin: 0;
  color: var(--sv-primary);
  letter-spacing: 0.18em;
  text-transform: uppercase;
  font-size: 0.82rem;
  font-weight: 700;
}

.service-hero h1 {
  margin: 14px 0 10px;
  color: var(--sv-dark);
  font-size: clamp(2rem, 4.8vw, 3.9rem);
  line-height: 1.1;
}

.hero-summary {
  margin: 0 auto;
  max-width: 760px;
  color: var(--sv-muted);
  line-height: 1.72;
}

.flow-section {
  padding: 0 5% 32px;
}

.flow-shell {
  max-width: 1220px;
  margin: 0 auto;
}

.flow-image-wrap {
  display: flex;
  justify-content: center;
  align-items: center;
  border-radius: 18px;
  overflow: hidden;
  border: 1px solid var(--sv-card-border);
  box-shadow: 0 10px 28px rgba(90, 72, 55, 0.1);
  background: #fff;
}

.flow-image {
  display: block;
  width: min(100%, 1100px);
  max-height: 68vh;
  height: auto;
  object-fit: contain;
}

.stages-section {
  max-width: 1220px;
  margin: 0 auto;
  padding: 28px 5% 90px;
  display: grid;
  gap: 26px;
}

.stage-block {
  display: grid;
  grid-template-columns: minmax(260px, 0.95fr) minmax(0, 1.05fr);
  gap: 22px;
  border-radius: 20px;
  border: 1px solid var(--sv-card-border);
  background: var(--sv-bg-soft);
  overflow: hidden;
}

.stage-reverse {
  grid-template-columns: minmax(0, 1.05fr) minmax(260px, 0.95fr);
}

.stage-reverse .stage-image-wrap {
  order: 2;
}

.stage-reverse .stage-content {
  order: 1;
}

.stage-image-wrap {
  min-height: 290px;
}

.stage-image-wrap img {
  width: 100%;
  height: 100%;
  display: block;
  object-fit: cover;
}

.stage-content {
  padding: 24px 24px 22px;
}

.stage-step {
  margin: 0;
  color: var(--sv-accent);
  font-size: 0.8rem;
  text-transform: uppercase;
  letter-spacing: 0.14em;
  font-weight: 700;
}

.stage-content h3 {
  margin: 10px 0 8px;
  color: var(--sv-dark);
  font-size: clamp(1.35rem, 2.2vw, 2rem);
}

.stage-desc {
  margin: 0;
  color: var(--sv-text);
  line-height: 1.68;
}

.stage-content ul {
  margin: 12px 0 0;
  padding-left: 20px;
  color: var(--sv-muted);
  line-height: 1.7;
}

.consult-session-info {
  margin-top: 14px;
  padding: 10px 14px;
  border-radius: 10px;
  background: #f5ede3;
  border: 1px solid #e4cdb3;
  display: inline-block;
}

.consult-session-detail {
  margin: 0;
  color: var(--sv-primary);
  font-weight: 700;
  font-size: 0.95rem;
}

@media (max-width: 980px) {

  .stage-block,
  .stage-reverse {
    grid-template-columns: 1fr;
  }

  .stage-reverse .stage-image-wrap,
  .stage-reverse .stage-content {
    order: initial;
  }

  .stage-image-wrap {
    min-height: 220px;
  }
}

@media (max-width: 768px) {
  .container {
    padding-top: 94px;
  }

  .service-hero {
    padding: 62px 4% 36px;
  }

  .flow-section {
    padding: 0 4% 16px;
  }

  .flow-shell {
    padding: 0;
  }

  .stages-section {
    padding: 16px 4% 66px;
    gap: 18px;
  }

  .stage-content {
    padding: 18px;
  }

  .stage-content ul {
    margin-top: 10px;
  }
}
</style>
